import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faShare } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { EyeIcon } from "../../assets/iconComponents/EyeIcon";
import { ProcessingIcon } from "../../assets/iconComponents/Processing";
import { getUserAuth } from "../../recoil/selectors";
import {
  STANDALONE_FETAL_SEX_CARD_STATE,
  type StandaloneFetalSexCardState,
} from "../../recoil/types";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { themePalette } from "../../theme";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { ANEUPLOIDY_PAGE_URL } from "../../utils/urlConstants";
import { DetailedResultsPatientReviewModal } from "../DetailedResults/DetailedResultsPatientReviewModal";
import { useDelayedPatientReviewModal } from "../DetailedResults/useDelayedPatientReviewModal";
import { FetalSexConfettiLayer } from "../shared/FetalSexResultBlock/FetalSexConfettiLayer";
import {
  getFetalSexResultBlockValue,
  isTwinStandaloneFetalSex,
} from "../shared/FetalSexResultBlock/fetalSexResultUtils";
import { FetalSexResultBlock } from "../shared/FetalSexResultBlock/FetalSexResultBlock";
import { SendActionType } from "../shared/Services/SharedServices";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  TestResultIconAndTypography,
  TestResultsIcon,
} from "../TestResults/TestResults.styles";
import {
  StandaloneFetalSexBalloonMotion,
  StandaloneFetalSexBalloonButton,
  StandaloneFetalSexBalloonStage,
  StandaloneFetalSexCardContainer,
  StandaloneFetalSexCardTitle,
  StandaloneFetalSexDivider,
  StandaloneFetalSexHoldInstruction,
  StandaloneFetalSexProcessingStatus,
  StandaloneFetalSexRevealedStage,
  StandaloneFetalSexShareButton,
  StandaloneFetalSexShareCaption,
  StandaloneFetalSexShareSection,
  StandaloneFetalSexUndoButton,
} from "./StandaloneFetalSexCard.styles";
import { StandaloneFetalSexBalloonIcon } from "./StandaloneFetalSexBalloonIcon";
import { StandaloneFetalSexShareModal } from "./StandaloneFetalSexShareModal";
import { useStandaloneFetalSexBalloonPress } from "./useStandaloneFetalSexBalloonPress";
import { useStandaloneFetalSexResult } from "./useStandaloneFetalSexResult";

interface StandaloneFetalSexCardProps {
  requisitionID: string;
}

const ANALYTICS_PAGE_NAME = "LandingPage";
export const REVEAL_ACTION_TIMEOUT_MS = 10000;

// After the patient completes the reveal hold, wait this long before showing
// the review modal.
const PATIENT_REVIEW_MODAL_DELAY_MS = 10000;
const CARD_STATE = STANDALONE_FETAL_SEX_CARD_STATE;

type StandaloneFetalSexCardBodyState = Exclude<
  StandaloneFetalSexCardState,
  typeof CARD_STATE.HIDDEN
>;
type CardBodyRenderer = () => React.ReactNode | null;
type CardBodyRenderers = Record<
  StandaloneFetalSexCardBodyState,
  CardBodyRenderer
>;

export const StandaloneFetalSexCard = ({
  requisitionID,
}: StandaloneFetalSexCardProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const auth = useRecoilValue(getUserAuth);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareRequestInFlight, setIsShareRequestInFlight] = useState(false);
  const { cardState, result } = useStandaloneFetalSexResult(requisitionID);
  const normalizedCardState =
    (cardState as string | undefined) === "SHOW_FETAL_SEX"
      ? CARD_STATE.SEX_REVEAL
      : cardState;
  const standaloneFetalSex = result?.standalone_fetal_sex;
  const fetalSexResultBlockValue =
    normalizedCardState === CARD_STATE.SEX_REVEAL
      ? getFetalSexResultBlockValue(standaloneFetalSex)
      : undefined;
  const lastTrackedCardImpressionRef = useRef("");

  const trackCardInteraction = (
    interactionType: string,
    elementName: string,
    properties: Record<string, string | number> = {},
  ): void => {
    if (!auth?.access_token) {
      return;
    }
    TrackAnalyticsEvent(
      AnalyticsEventNames.interaction,
      auth.access_token,
      auth.user_id,
      {
        interaction_type: interactionType,
        element_name: elementName,
        position: "StandaloneFetalSexCard",
        page_name: ANALYTICS_PAGE_NAME,
        ...properties,
      },
    );
  };

  useEffect(() => {
    const isRenderableState =
      normalizedCardState === CARD_STATE.PROCESSING ||
      (normalizedCardState === CARD_STATE.NON_SEX_RESULT && Boolean(result)) ||
      (normalizedCardState === CARD_STATE.SEX_REVEAL &&
        Boolean(fetalSexResultBlockValue));
    const impressionKey = `${requisitionID}:${normalizedCardState ?? ""}`;
    if (
      !auth?.access_token ||
      !isRenderableState ||
      lastTrackedCardImpressionRef.current === impressionKey
    ) {
      return;
    }

    lastTrackedCardImpressionRef.current = impressionKey;
    TrackAnalyticsEvent(
      AnalyticsEventNames.impression,
      auth.access_token,
      auth.user_id,
      {
        impression_type: "card_view",
        element_name: "standalone_fetal_sex_card",
        card_state: normalizedCardState ?? "unknown",
        page_name: ANALYTICS_PAGE_NAME,
      },
    );
  }, [
    auth?.access_token,
    auth?.user_id,
    fetalSexResultBlockValue,
    normalizedCardState,
    requisitionID,
    result,
  ]);

  const showFetalSexAction = async (): Promise<boolean> => {
    const abortController = new AbortController();
    const timeoutID = window.setTimeout(
      () => abortController.abort(),
      REVEAL_ACTION_TIMEOUT_MS,
    );
    try {
      const response = await SendActionType(
        "ShowFetalSex",
        requisitionID,
        auth?.access_token,
        {
          source: "standalone_fetal_sex_card",
        },
        abortController.signal,
      );
      if (!response.ok) {
        trackCardInteraction("request_result", "reveal_fetal_sex_failed", {
          failure_type: "http",
          response_status: response.status,
        });
        console.error("Failed to record standalone fetal sex reveal action", {
          responseStatus: response.status,
        });
        return false;
      }
      return true;
    } catch (error) {
      trackCardInteraction("request_result", "reveal_fetal_sex_failed", {
        failure_type: abortController.signal.aborted ? "timeout" : "network",
      });
      console.error("Failed to record standalone fetal sex reveal action", {
        error,
        failureType: abortController.signal.aborted ? "timeout" : "network",
      });
      return false;
    } finally {
      window.clearTimeout(timeoutID);
    }
  };

  const {
    balloonMotionRef,
    balloonPressHandlers,
    hideResult,
    isPressingBalloon,
    isResultRevealed,
  } = useStandaloneFetalSexBalloonPress(
    cardRef,
    () => {
      // Recording the interaction is best-effort. A tracking outage must never
      // prevent a patient from seeing a result they already completed the
      // reveal interaction for.
      showFetalSexAction().then((actionRecorded) => {
        if (actionRecorded) {
          trackCardInteraction("button_click", "reveal_fetal_sex", {
            button_text: "Press and hold to reveal your baby’s sex",
          });
        }
      });
      return true;
    },
    {
      onPressStart: (inputMethod) =>
        trackCardInteraction("press_start", "fetal_sex_balloon", {
          input_method: inputMethod,
        }),
      onPressCancel: (reason, holdDurationMs) =>
        trackCardInteraction("press_cancel", "fetal_sex_balloon", {
          cancel_reason: reason,
          hold_duration_ms: holdDurationMs,
        }),
    },
  );

  const { setShowPatientReviewModal, showPatientReviewModal } =
    useDelayedPatientReviewModal(
      PATIENT_REVIEW_MODAL_DELAY_MS,
      isResultRevealed,
    );

  const openShareModal = () => {
    setIsShareRequestInFlight(false);
    setIsShareModalOpen(true);
    trackCardInteraction("button_click", "share_fetal_sex", {
      button_text: "Share Fetal Sex",
      reveal_state: isResultRevealed ? "revealed" : "hidden",
    });
  };

  const hideFetalSex = () => {
    hideResult();
    trackCardInteraction("button_click", "hide_fetal_sex", {
      button_text: "Hide fetal sex",
    });
  };

  const renderProcessingCardBody = (): React.ReactNode => (
    <StandaloneFetalSexProcessingStatus>
      <ProcessingIcon />
      {GetTypographyContent({
        content: t("results.resultStatus.processing"),
        size: "medium",
        textColor: "greyMedium",
      })}
    </StandaloneFetalSexProcessingStatus>
  );

  const renderNonSexResultCardBody = (): React.ReactNode | null => {
    if (!result) {
      return null;
    }

    return (
      <TestResultIconAndTypography>
        <TestResultsIcon marginLeft>
          <EyeIcon color={themePalette.redDark} />
        </TestResultsIcon>
        {GetTypographyContent({
          content: t("results.resultStatus.seeReport"),
          size: "medium",
          textColor: "redDark",
          link: `${ANEUPLOIDY_PAGE_URL}/${requisitionID}`,
          onLinkClick: () =>
            trackCardInteraction(
              "link_click",
              "standalone_fetal_sex_see_report",
            ),
        })}
      </TestResultIconAndTypography>
    );
  };

  const renderSexRevealCardBody = (): React.ReactNode | null => {
    if (!fetalSexResultBlockValue) {
      return null;
    }

    if (!isResultRevealed) {
      const revealInstruction = t("fetalSex.standalone.reveal.holdInstruction");

      return (
        <StandaloneFetalSexBalloonStage>
          <StandaloneFetalSexBalloonButton
            aria-label={revealInstruction}
            data-pressing={isPressingBalloon}
            data-testid="standalone-fetal-sex-reveal-button"
            type="button"
            {...balloonPressHandlers}
          >
            <StandaloneFetalSexBalloonMotion ref={balloonMotionRef}>
              <StandaloneFetalSexBalloonIcon />
            </StandaloneFetalSexBalloonMotion>
          </StandaloneFetalSexBalloonButton>
          <StandaloneFetalSexHoldInstruction>
            {GetTypographyContent({
              content: revealInstruction,
              size: "small",
              textColor: "greyBody",
            })}
          </StandaloneFetalSexHoldInstruction>
        </StandaloneFetalSexBalloonStage>
      );
    }

    const isTwin = isTwinStandaloneFetalSex(fetalSexResultBlockValue);
    const leadInText = t(
      isTwin
        ? "fetalSex.standalone.reveal.leadInTwins"
        : "fetalSex.standalone.reveal.leadInSingle",
    );

    return (
      <StandaloneFetalSexRevealedStage>
        <FetalSexResultBlock
          fetalSex={fetalSexResultBlockValue}
          leadInText={leadInText}
        />
        <StandaloneFetalSexUndoButton
          aria-label={t("fetalSex.standalone.reveal.hide")}
          type="button"
          onClick={hideFetalSex}
        >
          <FontAwesomeIcon icon={faRotateLeft} />
        </StandaloneFetalSexUndoButton>
        {showPatientReviewModal && (
          <DetailedResultsPatientReviewModal
            requisitionID={requisitionID}
            showPatientReviewModal={showPatientReviewModal}
            setShowPatientReviewModal={setShowPatientReviewModal}
          />
        )}
      </StandaloneFetalSexRevealedStage>
    );
  };

  const cardBodyRenderers = {
    [CARD_STATE.PROCESSING]: renderProcessingCardBody,
    [CARD_STATE.NON_SEX_RESULT]: renderNonSexResultCardBody,
    [CARD_STATE.SEX_REVEAL]: renderSexRevealCardBody,
  } satisfies CardBodyRenderers;

  const cardBodyRenderer =
    normalizedCardState === CARD_STATE.HIDDEN ||
    normalizedCardState === undefined
      ? undefined
      : cardBodyRenderers[
          normalizedCardState as StandaloneFetalSexCardBodyState
        ];
  const cardBody = cardBodyRenderer?.() ?? null;

  if (cardBody === null || cardBody === undefined) {
    return null;
  }

  return (
    <StandaloneFetalSexCardContainer
      data-testid="standalone-fetal-sex-card"
      ref={cardRef}
    >
      {fetalSexResultBlockValue && isResultRevealed && (
        <FetalSexConfettiLayer />
      )}
      <Modal
        open={isShareModalOpen}
        disableEscapeKeyDown={isShareRequestInFlight}
        onClose={(_event, reason) => {
          if (isShareRequestInFlight) {
            return;
          }
          setIsShareModalOpen(false);
          trackCardInteraction("modal_dismiss", "share_fetal_sex", {
            dismiss_reason: reason,
          });
        }}
        disableScrollLock
        style={{
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        <StandaloneFetalSexShareModal
          requisitionID={requisitionID}
          onClose={() => setIsShareModalOpen(false)}
          onRequestInFlightChange={setIsShareRequestInFlight}
        />
      </Modal>
      <StandaloneFetalSexCardTitle>
        {GetTypographyContent({
          content: t("fetalSex.standalone.title"),
          size: "large",
          textColor: "greyMedium",
          isBold: true,
        })}
      </StandaloneFetalSexCardTitle>
      {cardBody}
      {fetalSexResultBlockValue && (
        <>
          <StandaloneFetalSexDivider />
          <StandaloneFetalSexShareSection>
            <StandaloneFetalSexShareButton
              type="button"
              onClick={openShareModal}
            >
              <FontAwesomeIcon icon={faShare} />
              {t("fetalSex.standalone.share.button")}
            </StandaloneFetalSexShareButton>
            <StandaloneFetalSexShareCaption>
              {GetTypographyContent({
                content: t("fetalSex.standalone.share.caption"),
                size: "small",
                textColor: "greyBody",
              })}
            </StandaloneFetalSexShareCaption>
          </StandaloneFetalSexShareSection>
        </>
      )}
    </StandaloneFetalSexCardContainer>
  );
};
